import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/store/AuthProvider';
import { PREIS, markenText, noetigeStufe, stufenHinweis, stufenName, usePlan, type Feature } from '@/store/plan';
import { cx } from '@/ui';
import { locale, useT } from '@/i18n';
import { useNow } from '@/hooks/useNow';

/**
 * Stufen-Marken (docs/21 §1): „Gesperrtes bleibt sichtbar" — der Knopf steht
 * da, ausgegraut, daneben eine kleine Marke „Pro" bzw. „Standard" mit Preis im
 * Tooltip; ein Klick auf die Marke führt zur Konto-Seite. Nichts verschwindet,
 * damit man sieht, was es gäbe, und nichts geht, was die Stufe nicht hergibt.
 */

/** Marke „Pro" / „Standard" mit Preis — klickbar zur Konto-Seite */
export function Marke({ feature, className, sticker }: { feature: Feature; className?: string; sticker?: boolean }) {
  const t = useT();
  const stufe = noetigeStufe(feature);
  return (
    <Link
      to="/konto"
      data-marke={stufe}
      className={cx(
        'label no-underline inline-flex items-center border leading-none cursor-pointer bg-panel',
        sticker ? 'px-1 py-px text-[9px] shadow-sm' : 'px-1.5 py-[2px]',
        stufe === 'pro' ? 'border-red text-red hover:bg-red/10' : 'border-blue text-blue hover:bg-blue/10',
        className,
      )}
      title={`${t(stufenHinweis(feature))} · ${t('Ab {stufe} ({preis} €/Jahr) — Konto → Stufe', { stufe: t(stufenName(stufe)), preis: PREIS[stufe] })}`}
      // Der Name beginnt mit dem sichtbaren Text (WCAG 2.5.3) — wer per Sprache „Pro" sagt, trifft die Marke
      aria-label={`${t(stufenName(stufe))}: ${t(stufenHinweis(feature))}`}
      onClick={(e) => e.stopPropagation()}
    >
      {t(stufenName(stufe))}
    </Link>
  );
}

/**
 * Ist das Merkmal in der Stufe drin? Für `helfer` und `mitorganisatoren` zählt
 * das Limit gegen die vorhandene Anzahl (`null` = unbegrenzt).
 */
export function useFreigabe(feature: Feature, anzahl = 0): boolean {
  const { features, limits } = usePlan();
  if (feature === 'helfer' || feature === 'mitorganisatoren') {
    const limit = limits[feature];
    return limit === null || anzahl < limit;
  }
  return features[feature];
}

/**
 * `<Gate feature="excel">…</Gate>`: Kinder normal, wenn das Merkmal da ist —
 * sonst ausgegraut, ohne Klick und Fokus (`inert`), und die Marke liegt **schräg
 * an der Ecke wie ein Aufkleber**, statt daneben Platz zu nehmen (Daniel,
 * 09/2026: „nimmt zu viel Platz und Sicht weg"). Früher lag er mittig quer
 * über dem Knopf und verdeckte dessen Beschriftung („Begeg[PRO]en") — jetzt
 * sitzt er rechts oben an der Ecke. Der Knopf behält so seine
 * Breite; wer mit der Maus darüber geht, liest im Tooltip, was es gäbe und ab
 * welcher Stufe, und ein Klick führt zum Konto. Bei `block` (ganze Zeilen,
 * etwa das Helfer-Formular) sitzt der Aufkleber rechts oben, damit er den
 * Text nicht verdeckt. `anzahl` nur für die Limits (Helfer, Mitorganisatoren).
 */
export function Gate({ feature, anzahl, children, className, block }: { feature: Feature; anzahl?: number; children: ReactNode; className?: string; block?: boolean }) {
  const offen = useFreigabe(feature, anzahl);
  if (offen) return <>{children}</>;
  const Tag = block ? 'div' : 'span';
  return (
    <Tag className={cx('relative', block ? 'flex' : 'inline-flex', 'items-center', className)} data-gate={feature}>
      <Tag inert className={cx('opacity-40 pointer-events-none select-none', block && 'flex-1 min-w-0')} aria-disabled="true">
        {children}
      </Tag>
      <Marke feature={feature} sticker className={cx('absolute z-[1]', block ? 'right-2 top-1.5 -rotate-6' : '-right-1.5 -top-2 -rotate-6')} />
    </Tag>
  );
}

/** Hinweiskarte über einer gesperrten Seite — Inhalt darunter bleibt lesbar */
export function StufenHinweis({ feature, className }: { feature: Feature; className?: string }) {
  const t = useT();
  return (
    <div className={cx('border border-line bg-panel2 px-4 py-3 text-sm flex items-center gap-3 flex-wrap', className)} data-stufen-hinweis={feature}>
      <Marke feature={feature} />
      <span className="text-muted flex-1 min-w-[12rem]">{t(stufenHinweis(feature))}</span>
      <Link to="/konto" className="text-blue whitespace-nowrap">
        {t('Zum Konto')}
      </Link>
    </div>
  );
}

/**
 * Kopfzeile der Turnierliste: die eigene Stufe auf einen Blick — „Pro · Test,
 * 41 Tage", „Standard bis 12.09.2027", „Frei". Nur mit Konto; ohne Login gäbe
 * es nichts zu zeigen außer „Frei", und das steht der Anmelden-Knopf schon dafür.
 */
export function StufenMarke({ className }: { className?: string }) {
  const t = useT();
  const auth = useAuth();
  const { konto, effektiv, laden } = usePlan();
  const now = useNow(60_000);
  if (!auth.available || !auth.user) return null;
  const text = markenText(konto?.plan ?? null, now, t, (ms) => new Date(ms).toLocaleDateString(locale()));
  const tone = effektiv === 'pro' ? 'border-red text-red' : effektiv === 'standard' ? 'border-blue text-blue' : 'border-line text-dim';
  return (
    <Link
      to="/konto"
      data-stufe={effektiv}
      className={cx('label no-underline inline-flex items-center border px-2 py-[3px] whitespace-nowrap hover:bg-panel2', tone, laden && !konto && 'opacity-60', className)}
      title={t('Deine Stufe — Konto → Stufe')}
    >
      {text}
    </Link>
  );
}
